import _asyncToGenerator from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/asyncToGenerator";
import _regeneratorRuntime from "/opt/build/repo/node_modules/@babel/runtime/regenerator";
import base64 from 'base64-arraybuffer';
import hkdf from './hkdf';
import aesGcm from './aes-gcm';
import ecdsa from './ecdsa';
import { stringToArrayBuffer, arrayBufferToString } from './utils';
var ECDH_ALGORITHM_NAME = 'ECDH';
var KEY_IS_EXTRACTABLE = true;
var KEY_WILL_BE_USED_TO = ['deriveKey', 'deriveBits'];
var PRIVATE_KEY_TYPE = 'jwk'; // pkcs8 not supported in firefox, must use jwk

var PUBLIC_KEY_TYPE = 'spki';
var ECDH_KEY_ENCRYPTION_KEY = 'ecdh-key-encryption-key';
/**
 * NIST recommendation:
 *
 * 128-bit security provided with 256-bit key size
 *
 * Pg. 55
 * https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-57pt1r5.pdf
 *
 **/

var NAMED_CURVE = 'P-256';
var ECDH_PARAMS = {
  name: ECDH_ALGORITHM_NAME,
  namedCurve: NAMED_CURVE
};

var generateKeyPair = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
    var keyPair;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) {
        switch (_context.prev = _context.next) {
          case 0:
            _context.next = 2;
            return window.crypto.subtle.generateKey(ECDH_PARAMS, KEY_IS_EXTRACTABLE, KEY_WILL_BE_USED_TO);

          case 2:
            keyPair = _context.sent;
            return _context.abrupt("return", keyPair);

          case 4:
          case "end":
            return _context.stop();
        }
      }
    }, _callee);
  }));

  return function generateKeyPair() {
    return _ref.apply(this, arguments);
  };
}();

var getRawPrivateKeyFromPrivateKey = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(privateKey) {
    var jwkPrivateKey, rawPrivateKey;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) {
        switch (_context2.prev = _context2.next) {
          case 0:
            _context2.next = 2;
            return window.crypto.subtle.exportKey(PRIVATE_KEY_TYPE, privateKey);

          case 2:
            jwkPrivateKey = _context2.sent;
            rawPrivateKey = stringToArrayBuffer(JSON.stringify(jwkPrivateKey));
            return _context2.abrupt("return", rawPrivateKey);

          case 5:
          case "end":
            return _context2.stop();
        }
      }
    }, _callee2);
  }));

  return function getRawPrivateKeyFromPrivateKey(_x) {
    return _ref2.apply(this, arguments);
  };
}();

var getPrivateKeyFromRawPrivateKey = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(rawPrivateKey) {
    var jwkPrivateKey, privateKey;
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) {
        switch (_context3.prev = _context3.next) {
          case 0:
            jwkPrivateKey = JSON.parse(arrayBufferToString(rawPrivateKey));
            _context3.next = 3;
            return window.crypto.subtle.importKey(PRIVATE_KEY_TYPE, jwkPrivateKey, ECDH_PARAMS, KEY_IS_EXTRACTABLE, KEY_WILL_BE_USED_TO);

          case 3:
            privateKey = _context3.sent;
            return _context3.abrupt("return", privateKey);

          case 5:
          case "end":
            return _context3.stop();
        }
      }
    }, _callee3);
  }));

  return function getPrivateKeyFromRawPrivateKey(_x2) {
    return _ref3.apply(this, arguments);
  };
}();

var getRawPublicKeyFromPublicKey = /*#__PURE__*/function () {
  var _ref4 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee4(publicKey) {
    var rawPublicKey;
    return _regeneratorRuntime.wrap(function _callee4$(_context4) {
      while (1) {
        switch (_context4.prev = _context4.next) {
          case 0:
            _context4.next = 2;
            return window.crypto.subtle.exportKey(PUBLIC_KEY_TYPE, publicKey);

          case 2:
            rawPublicKey = _context4.sent;
            return _context4.abrupt("return", rawPublicKey);

          case 4:
          case "end":
            return _context4.stop();
        }
      }
    }, _callee4);
  }));

  return function getRawPublicKeyFromPublicKey(_x3) {
    return _ref4.apply(this, arguments);
  };
}();

var getPublicKeyFromRawPublicKey = /*#__PURE__*/function () {
  var _ref5 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee5(rawPublicKey) {
    var publicKey;
    return _regeneratorRuntime.wrap(function _callee5$(_context5) {
      while (1) {
        switch (_context5.prev = _context5.next) {
          case 0:
            _context5.next = 2;
            return window.crypto.subtle.importKey(PUBLIC_KEY_TYPE, rawPublicKey, ECDH_PARAMS, KEY_IS_EXTRACTABLE, [] // empty list ok
            );

          case 2:
            publicKey = _context5.sent;
            return _context5.abrupt("return", publicKey);

          case 4:
          case "end":
            return _context5.stop();
        }
      }
    }, _callee5);
  }));

  return function getPublicKeyFromRawPublicKey(_x4) {
    return _ref5.apply(this, arguments);
  };
}();

var importEcdhKeyEncryptionKeyFromMaster = /*#__PURE__*/function () {
  var _ref6 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee6(masterKey, salt) {
    var keyEncryptionKey;
    return _regeneratorRuntime.wrap(function _callee6$(_context6) {
      while (1) {
        switch (_context6.prev = _context6.next) {
          case 0:
            _context6.next = 2;
            return aesGcm.importKeyFromMaster(masterKey, salt, ECDH_KEY_ENCRYPTION_KEY);

          case 2:
            keyEncryptionKey = _context6.sent;
            return _context6.abrupt("return", keyEncryptionKey);

          case 4:
          case "end":
            return _context6.stop();
        }
      }
    }, _callee6);
  }));

  return function importEcdhKeyEncryptionKeyFromMaster(_x5, _x6) {
    return _ref6.apply(this, arguments);
  };
}();

var generateEcdhKeyData = /*#__PURE__*/function () {
  var _ref7 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee7(masterKey, ecdsaPrivateKey) {
    var ecdhKeyPair, ecdhKeyEncryptionKeySalt, ecdhKeyEncryptionKey, ecdhRawPrivateKey, encryptedEcdhPrivateKey, ecdhPublicKey, signedEcdhPublicKey;
    return _regeneratorRuntime.wrap(function _callee7$(_context7) {
      while (1) {
        switch (_context7.prev = _context7.next) {
          case 0:
            _context7.next = 2;
            return generateKeyPair();

          case 2:
            ecdhKeyPair = _context7.sent;
            // derive a key encryption key using HKDF to encrypt the ECDH private key and store it on server
            ecdhKeyEncryptionKeySalt = hkdf.generateSalt();
            _context7.next = 6;
            return importEcdhKeyEncryptionKeyFromMaster(masterKey, ecdhKeyEncryptionKeySalt);

          case 6:
            ecdhKeyEncryptionKey = _context7.sent;
            _context7.next = 9;
            return getRawPrivateKeyFromPrivateKey(ecdhKeyPair.privateKey);

          case 9:
            ecdhRawPrivateKey = _context7.sent;
            _context7.next = 12;
            return aesGcm.encrypt(ecdhKeyEncryptionKey, ecdhRawPrivateKey);

          case 12:
            encryptedEcdhPrivateKey = _context7.sent;
            _context7.next = 15;
            return getRawPublicKeyFromPublicKey(ecdhKeyPair.publicKey);

          case 15:
            ecdhPublicKey = _context7.sent;
            _context7.next = 18;
            return ecdsa.sign(ecdsaPrivateKey, ecdhPublicKey);

          case 18:
            signedEcdhPublicKey = _context7.sent;
            return _context7.abrupt("return", {
              ecdhPrivateKey: ecdhKeyPair.privateKey,
              ecdhPublicKey: base64.encode(ecdhPublicKey),
              encryptedEcdhPrivateKey: base64.encode(encryptedEcdhPrivateKey),
              signedEcdhPublicKey: base64.encode(signedEcdhPublicKey),
              ecdhKeyEncryptionKeySalt: base64.encode(ecdhKeyEncryptionKeySalt)
            });

          case 20:
          case "end":
            return _context7.stop();
        }
      }
    }, _callee7);
  }));

  return function generateEcdhKeyData(_x7, _x8) {
    return _ref7.apply(this, arguments);
  };
}();

var computeSharedKeyEncryptionKey = /*#__PURE__*/function () {
  var _ref8 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee8(otherEcdhPublicKey, ecdhPrivateKey) {
    var sharedKeyEncryptionKey;
    return _regeneratorRuntime.wrap(function _callee8$(_context8) {
      while (1) {
        switch (_context8.prev = _context8.next) {
          case 0:
            _context8.next = 2;
            return window.crypto.subtle.deriveKey({
              name: ECDH_ALGORITHM_NAME,
              namedCurve: NAMED_CURVE,
              public: otherEcdhPublicKey
            }, ecdhPrivateKey, aesGcm.getEncryptionKeyParams(), !KEY_IS_EXTRACTABLE, aesGcm.KEY_WILL_BE_USED_TO);

          case 2:
            sharedKeyEncryptionKey = _context8.sent;
            return _context8.abrupt("return", sharedKeyEncryptionKey);

          case 4:
          case "end":
            return _context8.stop();
        }
      }
    }, _callee8);
  }));

  return function computeSharedKeyEncryptionKey(_x9, _x10) {
    return _ref8.apply(this, arguments);
  };
}();

export default {
  generateKeyPair: generateKeyPair,
  generateEcdhKeyData: generateEcdhKeyData,
  importEcdhKeyEncryptionKeyFromMaster: importEcdhKeyEncryptionKeyFromMaster,
  getPrivateKeyFromRawPrivateKey: getPrivateKeyFromRawPrivateKey,
  getRawPublicKeyFromPublicKey: getRawPublicKeyFromPublicKey,
  getPublicKeyFromRawPublicKey: getPublicKeyFromRawPublicKey,
  computeSharedKeyEncryptionKey: computeSharedKeyEncryptionKey
};